import { Fragment, useState, useEffect } from "react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, UserCheck, UserPen, UserX, Building2, DollarSign, Calendar, CalendarClock, UserCog, CheckCircle, RotateCcw, X } from "lucide-react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { AssigneeSummary } from "@/components/shared/AssigneeSummary";
import { useAppointmentActions } from "../hooks/useAppointmentActions";
import { AssignmentModal } from "@/components/shared/assignment-modal";
import { ConfirmUnassignDialog } from "@/components/shared/ConfirmUnassignDialog";
import { appointment_status } from "@/models/appointment.model";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "sonner";
import AdjustAppointmentDialog from "./AdjustAppointmentDialog";
import RescheduleAppointmentModal from "./RescheduleAppointmentModal";
import ChangeScheduledDateModal from "./ChangeScheduledDateModal";
import MarkAsGobackModal from "./MarkAsGobackModal";
import CancelAppointmentModal from "./CancelAppointmentModal";
import CompleteAppointmentModal from "./CompleteAppointmentModal";
import { completeAppointment, markAppointmentDone } from "@/services/appointment-completion.service";
import { fieldActionMessage, isConflictRefusal, isReasonRequired } from "@/lib/appointment-field-actions";
import { isCancellable, NOT_CANCELLABLE_REASON } from "@/lib/appointment-cancellation";
import { isReschedulable, NOT_RESCHEDULABLE_REASON } from "@/lib/appointment-reschedule";
import { isAssignable } from "@/lib/appointment-assignment";

interface VendorAppointmentActionsDropdownProps {
  appointment: AppointmentJoin;
  type: "assignment" | "actions";
  onAppointmentUpdate?: (updatedAppointment: AppointmentJoin) => void;
  onRefetch?: () => void;
}

export default function VendorAppointmentActionsDropdown({
  appointment,
  type,
  onAppointmentUpdate,
  onRefetch,
}: VendorAppointmentActionsDropdownProps) {
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [changeScheduledDateModalOpen, setChangeScheduledDateModalOpen] = useState(false);
  const [markAsGobackModalOpen, setMarkAsGobackModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completionRefusal, setCompletionRefusal] = useState<string | null>(null);
  const [confirmUnassignOpen, setConfirmUnassignOpen] = useState(false);

  // Handle appointment update from modals
  const handleAppointmentUpdate = (updatedAppointment: AppointmentJoin) => {
    if (onAppointmentUpdate) {
      onAppointmentUpdate(updatedAppointment);
    }
    // Trigger refetch to ensure list is updated
    if (onRefetch) {
      onRefetch();
    }
  };

  const {
    assigningResource,
    handleAssignResource,
    handleUnassignResource,
    canAssignResources,
    canUnassignResource,
    hasAssignment,
  } = useAppointmentActions({
    appointment,
    onAppointmentUpdate: handleAppointmentUpdate,
  });

  const status = appointment.status as appointment_status;

  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (
      adjustDialogOpen ||
      rescheduleModalOpen ||
      changeScheduledDateModalOpen ||
      assignmentModalOpen ||
      markAsGobackModalOpen ||
      cancelModalOpen ||
      completeModalOpen
    ) {
      setDropdownOpen(false);
    }
  }, [
    adjustDialogOpen,
    rescheduleModalOpen,
    changeScheduledDateModalOpen,
    assignmentModalOpen,
    markAsGobackModalOpen,
    cancelModalOpen,
    completeModalOpen,
  ]);

  const isDropdownDisabled = status === appointment_status.cancelled;

  const handleMarkAsDone = async () => {
    try {
      handleAppointmentUpdate(await markAppointmentDone(appointment.id));
      toast.success("Appointment marked as done");
    } catch (error) {
      toast.error(fieldActionMessage(error, "Failed to mark appointment as done"));
      if (isConflictRefusal(error)) onRefetch?.();
    }
  };

  const handleMarkAsComplete = async () => {
    if (appointment.billing_exception) {
      setCompleteModalOpen(true);
      return;
    }
    try {
      handleAppointmentUpdate(await completeAppointment(appointment.id));
      toast.success("Appointment marked as complete");
    } catch (error) {
      if (isReasonRequired(error)) {
        setCompletionRefusal(fieldActionMessage(error, "Give a reason to complete this appointment."));
        setCompleteModalOpen(true);
        onRefetch?.();
        return;
      }
      toast.error(fieldActionMessage(error, "Failed to complete appointment"));
    }
  };

  // Handle cancel appointment - opens modal to collect cancellation reason
  const handleCancelAppointment = () => {
    setCancelModalOpen(true);
  };

  if (type === "assignment") {
    if (!isAssignable(status) || (!canAssignResources && !hasAssignment)) {
      return (
        <div className="flex items-center gap-2">
          {hasAssignment ? (
            <div className="flex items-center gap-2">
              <AssigneeSummary appointment={appointment} />
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="flex items-center gap-2">
          {hasAssignment ? (
            // Show current assignment with edit button
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 mr-2">
                <AssigneeSummary appointment={appointment} />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAssignmentModalOpen(true);
                }}
                disabled={assigningResource}
              >
                <UserPen className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            // Show assign button
            <Button
              variant="outline"
              onClick={() => {
                setAssignmentModalOpen(true);
              }}
              disabled={assigningResource}
            >
              <UserCheck className="w-3 h-3 mr-1" />
              Assign
            </Button>
          )}
        </div>

        {/* Assignment Modal - Edit mode when there's an assignment */}
        <AssignmentModal
          isOpen={assignmentModalOpen}
          onClose={() => {
            setAssignmentModalOpen(false);
          }}
          appointment={appointment}
          onAssign={handleAssignResource}
          isLoading={assigningResource}
          isEditMode={hasAssignment}
        />
      </>
    );
  }

  type ActionItem = {
    key: string;
    label: string;
    onSelect: () => void;
    icon?: ReactNode;
    disabled?: boolean;
    reason?: string;
    className?: string;
  };

  const actionItems: ActionItem[] = [];

  const adjustItem: ActionItem = {
    key: "adjust_service_price_payout",
    label: "Adjust service, price and payout",
    onSelect: () => setAdjustDialogOpen(true),
    icon: <DollarSign className="mr-2 h-4 w-4" />,
  };

  // For "inprogress" status, only show specific actions
  if (status === appointment_status.inprogress) {
    actionItems.push(adjustItem);

    // Change Resource and Payment
    actionItems.push({
      key: "change_resource_payment",
      label: "Change Resource and Payment",
      onSelect: () => {
        setAssignmentModalOpen(true);
      },
      icon: <UserCog className="mr-2 h-4 w-4" />,
    });

    // Separator before Update Status options
    actionItems.push({ key: "__separator__", label: "", onSelect: () => {} });

    actionItems.push({
      key: "mark_as_done",
      label: "Mark as Done",
      onSelect: () => {
        void handleMarkAsDone();
      },
      icon: <CheckCircle className="mr-2 h-4 w-4" />,
    });
  }
  // For "done" status, allow the same edit modals plus "Mark as Go Back"
  else if (status === appointment_status.done) {
    actionItems.push(adjustItem);

    actionItems.push({
      key: "change_resource_payment",
      label: "Change Resource and Payment",
      onSelect: () => {
        setAssignmentModalOpen(true);
      },
      icon: <UserCog className="mr-2 h-4 w-4" />,
    });

    actionItems.push({ key: "__separator__", label: "", onSelect: () => {} });
    actionItems.push({
      key: "mark_as_complete",
      label: "Mark as Complete",
      onSelect: () => {
        void handleMarkAsComplete();
      },
      icon: <CheckCircle className="mr-2 h-4 w-4" />,
    });
    actionItems.push({
      key: "mark_as_goback",
      label: "Mark as Go Back",
      onSelect: () => {
        setMarkAsGobackModalOpen(true);
      },
      icon: <RotateCcw className="mr-2 h-4 w-4" />,
    });
  } else if (status === appointment_status.completed) {
    actionItems.push(adjustItem);
    actionItems.push({ key: "__separator__", label: "", onSelect: () => {} });
    actionItems.push({
      key: "mark_as_goback",
      label: "Mark as Go Back",
      onSelect: () => {
        setMarkAsGobackModalOpen(true);
      },
      icon: <RotateCcw className="mr-2 h-4 w-4" />,
    });
  } else if (status === appointment_status.goback) {
    actionItems.push(adjustItem);
    actionItems.push({
      key: "change_goback_date",
      label: "Change Go Back Date",
      onSelect: () => {
        setChangeScheduledDateModalOpen(true);
      },
      icon: <CalendarClock className="mr-2 h-4 w-4" />,
    });

    actionItems.push({ key: "__separator__", label: "", onSelect: () => {} });

    actionItems.push({
      key: "mark_as_done",
      label: "Mark as Done",
      onSelect: () => {
        void handleMarkAsDone();
      },
      icon: <CheckCircle className="mr-2 h-4 w-4" />,
    });
  } else {
    actionItems.push(adjustItem);

    actionItems.push({
      key: "reschedule",
      label: "Reschedule Appointment",
      onSelect: () => {
        setRescheduleModalOpen(true);
      },
      icon: <Calendar className="mr-2 h-4 w-4" />,
      disabled: !isReschedulable(status),
      reason: isReschedulable(status) ? undefined : NOT_RESCHEDULABLE_REASON,
    });

    const canChangeScheduledDate = isReschedulable(status);
    actionItems.push({
      key: "change_scheduled_date",
      label: "Change Scheduled Date",
      onSelect: () => {
        setChangeScheduledDateModalOpen(true);
      },
      icon: <CalendarClock className="mr-2 h-4 w-4" />,
      disabled: !canChangeScheduledDate,
      reason: canChangeScheduledDate ? undefined : "This action is only available before work starts.",
    });

    // Change Resource and Payment
    actionItems.push({
      key: "change_resource_payment",
      label: "Change Resource and Payment",
      onSelect: () => {
        setAssignmentModalOpen(true);
      },
      icon: <UserCog className="mr-2 h-4 w-4" />,
    });

    if (canUnassignResource) {
      actionItems.push({
        key: "unassign_resource",
        label: "Unassign Resource",
        onSelect: () => setConfirmUnassignOpen(true),
        icon: <UserX className="mr-2 h-4 w-4" />,
      });
    }

    // Separator before Update Status options
    actionItems.push({ key: "__separator__", label: "", onSelect: () => {} });

    actionItems.push({
      key: "mark_as_done",
      label: "Mark as Done",
      onSelect: () => {
        void handleMarkAsDone();
      },
      icon: <CheckCircle className="mr-2 h-4 w-4" />,
      disabled: status === appointment_status.cancelled,
    });

    actionItems.push({
      key: "cancel",
      label: "Cancel Appointment",
      onSelect: () => {
        void handleCancelAppointment();
      },
      icon: <X className="mr-2 h-4 w-4" />,
      className: "text-red-600 focus:bg-red-50",
      disabled: !isCancellable(status),
      reason: isCancellable(status) ? undefined : NOT_CANCELLABLE_REASON,
    });
  }

  const renderMenuItem = (item: ActionItem) => {
    if (item.key === "__separator__") {
      return <DropdownMenuSeparator key="vendor-actions-separator" />;
    }

    const menu = (
      <DropdownMenuItem
        onSelect={(e) => {
          if (!item.disabled) {
            // Close dropdown immediately
            setDropdownOpen(false);
            // Execute action immediately - dropdown will close automatically
            item.onSelect();
          }
        }}
        disabled={item.disabled}
        className={item.className}
      >
        {item.icon}
        {item.label}
      </DropdownMenuItem>
    );

    if (item.disabled && item.reason) {
      return (
        <Tooltip key={item.key}>
          <TooltipTrigger asChild>
            <span className="block cursor-not-allowed" title={item.reason}>
              {menu}
            </span>
          </TooltipTrigger>
          <TooltipContent>{item.reason}</TooltipContent>
        </Tooltip>
      );
    }

    return <Fragment key={item.key}>{menu}</Fragment>;
  };

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        {isDropdownDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0" disabled>
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              No actions available for this appointment.
            </TooltipContent>
          </Tooltip>
        ) : (
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
          {actionItems.length === 0 ? (
            <DropdownMenuItem disabled>No actions available</DropdownMenuItem>
          ) : (
            actionItems.map(renderMenuItem)
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AdjustAppointmentDialog
        isOpen={adjustDialogOpen}
        onClose={() => setAdjustDialogOpen(false)}
        appointment={appointment}
        onSuccess={handleAppointmentUpdate}
        onRefetch={onRefetch}
      />

      {/* Reschedule Appointment Modal */}
      <RescheduleAppointmentModal
        isOpen={rescheduleModalOpen}
        onClose={() => setRescheduleModalOpen(false)}
        appointment={appointment}
        onSuccess={handleAppointmentUpdate}
      />

      {/* Change Scheduled Date / Go Back Date Modal */}
      <ChangeScheduledDateModal
        isOpen={changeScheduledDateModalOpen}
        onClose={() => setChangeScheduledDateModalOpen(false)}
        appointment={appointment}
        onSuccess={handleAppointmentUpdate}
        title={status === appointment_status.goback ? "Change Go Back Date" : undefined}
        description={status === appointment_status.goback ? "Select a new date for the go back appointment." : undefined}
      />

      {/* Change Resource and Payment Modal */}
      <AssignmentModal
        isOpen={assignmentModalOpen}
        onClose={() => setAssignmentModalOpen(false)}
        appointment={appointment}
        onAssign={handleAssignResource}
        isLoading={assigningResource}
        isEditMode={hasAssignment}
      />

      {/* Mark as Go Back Modal */}
      <MarkAsGobackModal
        isOpen={markAsGobackModalOpen}
        onClose={() => setMarkAsGobackModalOpen(false)}
        appointment={appointment}
        onSuccess={handleAppointmentUpdate}
      />

      <CompleteAppointmentModal
        isOpen={completeModalOpen}
        onClose={() => {
          setCompleteModalOpen(false);
          setCompletionRefusal(null);
        }}
        appointment={appointment}
        refusal={completionRefusal}
        onSuccess={handleAppointmentUpdate}
      />

      {/* Cancel Appointment Modal */}
      <CancelAppointmentModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        appointment={appointment}
        onSuccess={handleAppointmentUpdate}
        onRefetch={onRefetch}
      />

      <ConfirmUnassignDialog open={confirmUnassignOpen} onOpenChange={setConfirmUnassignOpen} onConfirm={() => void handleUnassignResource()} busy={assigningResource} />
    </>
  );
}
